import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useShelves } from '@/api/hooks/useShelves'
import type { WorkOrder } from '@/api/hooks/useWorkOrder'
import { OptionButton } from '@/components/molecules/option-button'
import { decodeSparePartAnswers, encodeSparePartAnswer } from './checkpoint-question.utils'
import { type FieldProps, QuestionFieldShell } from './checkpoint-question-fields'

export type CheckpointSparePart = WorkOrder['spareParts'][number]

type Props = FieldProps & {
  spareParts: CheckpointSparePart[]
  workshopId: string
}

/**
 * Lists the work order's spare parts with a Ja/Nej choice per part. Submitting
 * the checkpoint writes each choice straight to the part: "Ja" marks it used and
 * clears its shelf, "Nej" marks it unused.
 */
export const SparePartsQuestion = ({
  question,
  answer,
  onChange,
  spareParts,
  workshopId,
}: Props) => {
  const { t } = useTranslation()
  const { data: shelves } = useShelves(workshopId)

  const shelfNameById = useMemo(
    () => new Map(shelves.map(shelf => [shelf.id, shelf.name])),
    [shelves],
  )

  const usedByPartId = useMemo(
    () => new Map(decodeSparePartAnswers(answer.value).map(a => [a.sparePartId, a.used])),
    [answer.value],
  )

  const positionOf = (part: CheckpointSparePart) =>
    (part.shelfId ? shelfNameById.get(part.shelfId) : undefined) ??
    t('checkpointQuestion.spareParts.noShelf')

  const select = (part: CheckpointSparePart, used: boolean) => {
    const next = new Map(usedByPartId).set(part.id, used)
    // Rebuild in list order so the stored answer always mirrors what was shown.
    onChange({
      ...answer,
      value: spareParts
        .filter(p => next.has(p.id))
        .map(p =>
          encodeSparePartAnswer(p.id, next.get(p.id)!, `${p.description} — ${positionOf(p)}`),
        ),
    })
  }

  return (
    <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="stacked">
      {spareParts.length === 0 ? (
        <p className="text-sm text-text-secondary italic">
          {t('checkpointQuestion.spareParts.noParts')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {spareParts.map(part => {
            const used = usedByPartId.get(part.id)
            return (
              <div
                key={part.id}
                className="flex items-center gap-3 rounded-lg border border-strong-01 bg-field-2 p-3"
              >
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{part.description}</span>
                  <span className="block text-xs text-text-secondary truncate">
                    {positionOf(part)}
                  </span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <OptionButton
                    text={t('global.yes')}
                    selected={used === true}
                    onClick={() => select(part, true)}
                  />
                  <OptionButton
                    text={t('global.no')}
                    selected={used === false}
                    onClick={() => select(part, false)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </QuestionFieldShell>
  )
}
